import {
  Button,
  Typography,
  TypographyMods,
  TypographySizes,
} from '@vk-cloud-solutions/marketing-ui';
import classNames from 'classnames';
import React from 'react';

import { FormField } from '~components/Forms/FormField';
import { FormInput } from '~components/Forms/FormInput';
import { FormInputMods } from '~components/Forms/FormInput/types';
import { NextImage } from '~components/NextImage/NextImage';
import { NextImageMods } from '~components/NextImage/NextImage.types';
import { PreloaderOverlay } from '~components/PreloaderOverlay/PreloaderOverlay';
import { Validation } from '~helpers/forms/Validation';
import { useForm, UseFormField } from '~hooks/useForm';
import ReplayIcon from '~public/static/images/icons/Arrow_replay.svg';
import { BlogApi } from '~services/BlogApi';

import { SectionSubscribeProps } from '../SectionSubscribe.types';
import styles from './SectionSubscribeAside.module.scss';

export function SectionSubscribeAside({
  title,
  text,
  image,
  button,
  mailoutId,
}: SectionSubscribeProps) {
  const formFields: Record<string, UseFormField> = {
    email: {
      value: '',
      validate: Validation.required(Validation.email()),
    },
  };

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    handleBlur,
    status,
    setStatus,
    resetForm,
    isSubmitting,
    touched,
  } = useForm({
    fields: formFields,
    submit(data) {
      // const utm = getUtmFromUrl();

      BlogApi.subscribe({
        email: data.email,
        mailoutId: mailoutId,
      })
        .then(() => {
          setStatus({
            success: {
              title: 'Спасибо!',
              message: `Вы подписались на нашу рассылку.  Теперь вам
              необходимо подтвердить вашу подписку. На ваш
              email ${data.email} отправлено письмо
              с подтверждением`,
            },
          });
        })
        .catch((error) => {
          console.error(error);
          setStatus({
            error: {
              title: 'Ошибка!',
              message:
                'Произошла ошибка из-за ввода некорректных данных. \n' +
                'Пожалуйста, заполните форму повторно',
            },
          });
        });
    },
  });

  function handleResetClick() {
    return resetForm();
  }

  return (
    <div className={styles.container}>
      {status.success && (
        <div className={styles.content}>
          <Typography
            tagName={TypographySizes.h2}
            mods={[TypographyMods.bodyMAlt]}
            className={styles.title}
          >
            {status.success.title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.captionM]}
            className={styles.text}
          >
            {status.success.message}
          </Typography>
          {image && (
            <NextImage
              className={styles.image}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <Button
            className={classNames(styles.button, styles.buttonReset)}
            mod="secondary"
            onClick={handleResetClick}
            size="m"
            type="reset"
          >
            <ReplayIcon className={styles.buttonIcon} />
            Заполнить повторно
          </Button>
        </div>
      )}

      {status.error && (
        <div className={styles.content}>
          <Typography
            tagName={TypographySizes.h2}
            mods={[TypographyMods.bodyMAlt]}
            className={styles.title}
          >
            {status.error.title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.captionM]}
            className={styles.text}
          >
            {status.error.message}
          </Typography>
          {image && (
            <NextImage
              className={styles.image}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <Button
            className={classNames(styles.button, styles.buttonReset)}
            mod="secondary"
            onClick={handleResetClick}
            size="m"
            type="reset"
          >
            <ReplayIcon className={styles.buttonIcon} />
            Заполнить повторно
          </Button>
        </div>
      )}

      {!status.success && !status.error && (
        <div className={styles.content}>
          <PreloaderOverlay active={isSubmitting} />
          <Typography
            tagName={TypographySizes.h2}
            mods={[TypographyMods.bodyMAlt]}
            className={styles.title}
          >
            {title}
          </Typography>
          <Typography
            tagName={TypographySizes.p}
            mods={[TypographyMods.captionM]}
            className={styles.text}
          >
            {text}
          </Typography>
          {image && (
            <NextImage
              className={styles.image}
              mods={[NextImageMods.FitContain]}
              {...image}
            />
          )}
          <form onSubmit={handleSubmit} className={styles.form}>
            <FormField
              className={styles.formField}
              errors={touched.email && errors.email}
              label="Введите email"
            >
              <FormInput
                name="email"
                type="email"
                value={values.email}
                isError={Boolean(errors.email && touched.email)}
                onBlur={handleBlur}
                onChange={handleChange}
                mods={[FormInputMods.Lg]}
              />
            </FormField>
            {button && (
              <Button
                className={styles.button}
                text={button.text}
                type="submit"
                mod="tertiary"
                size="m"
                dataTest="sectionSubscribe.button"
              />
            )}
          </form>
        </div>
      )}
    </div>
  );
}
